import { useState, useEffect } from "react";
import { Badge } from "../components/ui/badge";
import RevealCeremony from "./RevealCeremony";
import {
  ShieldCheck,
  Heart,
  X,
  MapPin,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  UserX,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function DiscoveryView({ token, onMatch }) {
  const [profiles, setProfiles] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const [matchPopup, setMatchPopup] = useState(null);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/discovery`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setProfiles(data.profiles || []);
      setCurrentIdx(0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = async (action) => {
    if (swiping || !profiles[currentIdx]) return;
    setSwiping(true);
    try {
      const res = await fetch(`${API}/swipe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          target_user_id: profiles[currentIdx].id,
          action,
        }),
      });
      const data = await res.json();
      if (data.is_match) {
        setMatchPopup({
          profile: profiles[currentIdx],
          revealed: data.matched_user || null,
        });
        if (onMatch) onMatch();
        setTimeout(() => setMatchPopup(null), 6000);
      }
      setCurrentIdx((prev) => prev + 1);
    } catch (err) {
      console.error(err);
    } finally {
      setSwiping(false);
    }
  };

  const currentProfile = profiles[currentIdx];
  const noMore = !loading && (!profiles.length || currentIdx >= profiles.length);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#050505]">
        <p
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
          className="text-[#00FF66] text-sm tracking-widest uppercase animate-pulse"
        >
          Scanning verified profiles...
        </p>
      </div>
    );
  }

  if (noMore) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#050505] px-6">
        {/* Reveal Ceremony can appear even after the last profile is swiped */}
        {matchPopup && (
          <RevealCeremony matchPopup={matchPopup} onClose={() => setMatchPopup(null)} />
        )}
        <Sparkles className="w-12 h-12 text-[#8A8A93] mb-4" />
        <h2
          style={{ fontFamily: "'Bodoni Moda', serif" }}
          className="text-2xl text-[#FAFAFA] mb-2"
        >
          No more profiles
        </h2>
        <p className="text-[#8A8A93] text-sm text-center max-w-sm">
          You've seen all verified profiles in your area. Check back later for
          new matches.
        </p>
      </div>
    );
  }

  return (
    <div className="swipe-container" data-testid="discovery-view">
      {/* Match Popup — with Reveal Ceremony for anonymous matches */}
      {matchPopup && (
        <RevealCeremony matchPopup={matchPopup} onClose={() => setMatchPopup(null)} />
      )}

      {/* Profile Card */}
      {currentProfile && (
        <div
          className="swipe-card animate-fade-in relative"
          data-testid="swipe-card"
        >
          {/* Blurred / anonymous photo layer */}
          {currentProfile.is_anonymous || !currentProfile.photos?.[0] ? (
            <div
              className="absolute inset-0 overflow-hidden"
              data-testid="anonymous-photo-placeholder"
            >
              {/* Base gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#1A0F2E] via-[#2A0F1D] to-[#0A0A0C]" />
              {/* Aurora radial blobs */}
              <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#FF3B6B]/25 blur-[120px]" />
              <div className="absolute bottom-0 -right-32 w-[500px] h-[500px] rounded-full bg-[#7C3AED]/25 blur-[120px]" />
              <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full bg-[#00E5FF]/10 blur-[100px]" />
              {/* Diagonal noise lines */}
              <div className="absolute inset-0 opacity-[0.06]" style={{
                backgroundImage: "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 1px, transparent 12px)"
              }} />
              {/* Center silhouette + label */}
              <div className="relative h-full flex flex-col items-center justify-center pb-32">
                <div className="w-32 h-32 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md flex items-center justify-center">
                  <UserX size={56} className="text-white/40" strokeWidth={1.2} />
                </div>
                <p
                  style={{ fontFamily: "'Bodoni Moda', serif" }}
                  className="mt-6 text-white/70 text-2xl italic tracking-tight"
                >
                  Identity revealed on match
                </p>
              </div>
            </div>
          ) : (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${currentProfile.photos[0]})`,
                filter: "blur(28px) saturate(1.1) brightness(0.85)",
                transform: "scale(1.15)",
              }}
              data-testid="blurred-discovery-photo"
            />
          )}
          {/* Dark vignette so text is readable */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/50 pointer-events-none" />

          {/* "Photos reveal on match" indicator */}
          <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20">
            <div
              data-testid="photo-blur-indicator"
              className="inline-flex items-center gap-2 rounded-full pl-3 pr-4 h-8 bg-black/70 backdrop-blur-xl border border-white/15"
            >
              <EyeOff size={12} className="text-[#FF3B6B]" />
              <span
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
                className="text-white/80 text-[10px] tracking-widest uppercase"
              >
                Photos reveal on match
              </span>
            </div>
          </div>

          {/* Verified Badge Overlay */}
          <div className="absolute top-6 left-6 z-20 flex flex-col gap-2 items-start mt-12">
            <div className="flex items-center gap-2 bg-black/60 backdrop-blur-xl px-3 py-1.5 border border-white/10">
              <div className="w-2 h-2 rounded-full bg-[#00FF66] pulse-badge" />
              <ShieldCheck className="w-4 h-4 text-[#00FF66]" />
              <span
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
                className="text-[#00FF66] text-xs tracking-widest uppercase"
              >
                FBI Verified
              </span>
            </div>

            {currentProfile.is_anonymous && (
              <div
                data-testid="anonymous-badge"
                className="flex items-center gap-2 bg-black/60 backdrop-blur-xl px-3 py-1.5 border border-[#7C3AED]/40"
              >
                <UserX className="w-3.5 h-3.5 text-[#B084FF]" />
                <span
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  className="text-[#B084FF] text-xs tracking-widest uppercase font-bold"
                >
                  Anonymous
                </span>
              </div>
            )}

            {/* Compatibility Score */}
            {typeof currentProfile.compatibility_score === "number" && (
              <div
                data-testid="compatibility-score"
                className="flex items-center gap-2 bg-black/60 backdrop-blur-xl px-3 py-1.5 border border-[#FF3B6B]/40"
              >
                <Heart className="w-3.5 h-3.5 text-[#FF3B6B] fill-[#FF3B6B]" />
                <span
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  className="text-[#FF3B6B] text-xs tracking-widest uppercase font-bold"
                >
                  {currentProfile.compatibility_score}% Match
                </span>
              </div>
            )}

            {/* Last Active */}
            {currentProfile.last_active_at && (
              <div
                data-testid="last-active"
                className="flex items-center gap-2 bg-black/60 backdrop-blur-xl px-3 py-1.5 border border-white/10"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#00FF66] animate-pulse" />
                <span
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  className="text-[#FAFAFA]/80 text-[10px] tracking-widest uppercase"
                >
                  Active {timeAgo(currentProfile.last_active_at)}
                </span>
              </div>
            )}
          </div>

          {/* Profile counter */}
          <div className="absolute top-6 right-6 z-20">
            <span
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
              className="text-[#8A8A93] text-xs"
            >
              {currentIdx + 1} / {profiles.length}
            </span>
          </div>

          {/* HUD Overlay */}
          <div className="swipe-hud">
            <div className="max-w-lg">
              <div className="flex items-baseline gap-3 mb-2">
                <h2
                  style={{ fontFamily: "'Bodoni Moda', serif" }}
                  className="text-4xl sm:text-5xl text-[#FAFAFA] tracking-tighter"
                >
                  {currentProfile.name}
                </h2>
                <span className="text-2xl text-[#8A8A93]">
                  {currentProfile.age}
                </span>
              </div>

              {currentProfile.location && (
                <div className="flex items-center gap-1.5 text-[#8A8A93] text-sm mb-3">
                  <MapPin size={14} />
                  <span>{currentProfile.location}</span>
                </div>
              )}

              <p className="text-[#FAFAFA]/80 text-sm leading-relaxed mb-4 max-w-md">
                {currentProfile.bio}
              </p>

              {currentProfile.interests?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {currentProfile.interests.map((interest) => (
                    <span
                      key={interest}
                      className="px-2.5 py-1 text-xs border border-white/20 text-[#FAFAFA]/70 bg-white/5"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="swipe-actions">
              <button
                data-testid="swipe-pass-btn"
                className="swipe-btn pass"
                onClick={() => handleSwipe("pass")}
                disabled={swiping}
              >
                <X size={28} />
              </button>
              <button
                data-testid="swipe-like-btn"
                className="swipe-btn like"
                onClick={() => handleSwipe("like")}
                disabled={swiping}
              >
                <Heart size={28} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
